import { Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output } from '@angular/core';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { combineLatest, Observable, Observer, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FileUploadInformation, FileUploadService } from 'modules/fileupload/fileupload.service';
import { ApplicationService, AttachmentDeadlineInfo, AttachmentTypeInfo, FileAttachmentInfo, NomineeFileInfo, NomineeInfo, NomineeTypeInfo } from 'services/application/application.service';
import { ConfigService } from 'services/config/config.service';
import { DialogService } from 'services/dialogs/dialogs.service';
import { DownloadService, HttpMethod, DownloadFormat } from 'services/download/download.service';
import { ErrorService } from 'services/error/error.service';
import { ScreenSizeService } from 'services/screensize/screensize.service';
import { SplashService } from 'services/splash/splash.service';
import { UserInfo } from 'services/user/user.service';
import { FileSizePipe } from 'pipes/filesize.pipe';
import { DateFnsPipe } from 'pipes/datefns.pipe';

@Component({
	selector: 'app-attachment-panel',
	styleUrls: ['attachmentpanel.component.scss'],
	templateUrl: 'attachmentpanel.component.html',
	animations: [
		trigger('fadeInOut', [
			state('true', style({ opacity: 1 })),
			state('false', style({ opacity: 0 })),
			transition('false => true', animate(300)),
			transition('true => false', animate(1500))
		])
	]
})

export class AttachmentPanelComponent implements OnChanges, OnInit, OnDestroy {
	private _subrelease: Subject<void> = new Subject<void>();

	@Input() AttachmentType: AttachmentTypeInfo;
	@Input() AwardID: number;
	@Input() CanDownload: boolean;
	@Input() Instructions: string;
	@Input() NomineeInfo: NomineeInfo;
	@Input() NomineeType: NomineeTypeInfo;
	@Input() FilesInfo: NomineeFileInfo[];
	@Input() FileWindowDeadline: AttachmentDeadlineInfo;
	@Input() SiteName: string;
	@Input() User: UserInfo;

	@Output() FilesInfoChange = new EventEmitter<NomineeFileInfo[]>();
	@Output() removed = new EventEmitter<FileAttachmentInfo>();
	@Output() uploaded = new EventEmitter<FileAttachmentInfo>();

	AllHeaders: string[] = ['FileName', 'OriginalFileName', 'FileSize', 'DateUploaded', 'UploaderName', 'FileStatus', 'Actions'];
	Files: FileAttachmentInfo[];
	FileWindowOpen: boolean;
	HasMaxSize: boolean;
	HasMinSize: boolean;
	HasResolution: boolean;
	NoData: boolean;
	SmallSize: boolean;

	constructor(private appSvc: ApplicationService, private configSvc: ConfigService,
		private dateFnsPipe: DateFnsPipe, private dlgSvc: DialogService, private downloadSvc: DownloadService,
		private errSvc: ErrorService, private fileSizePipe: FileSizePipe,
		private screenSvc: ScreenSizeService, private splashSvc: SplashService, private uploadSvc: FileUploadService) {

		// Initialize the data source.
		this.NoData = true;
	}

	ngOnDestroy() {
		this._subrelease.next();
		this._subrelease.complete();
	}

	ngOnChanges() {
		const self = this;

		// Set our data source and trackers now.
		if (!self.AttachmentType || !self.FilesInfo || !self.FilesInfo.length) {
			self.Files = [];
		} else {
			const _files = self.FilesInfo.find(fi => fi.AttachmentTypeID === self.AttachmentType.AttachmentTypeID);

			self.Files = (_files ? _files.Files || [] : []);
		}

		self.NoData = (!self.Files || !self.Files.length);

		self.FileWindowOpen = (self.FileWindowDeadline && self.FileWindowDeadline.WindowOpen);
		self.HasMaxSize = self.AttachmentType && self.AttachmentType.SizeLimit != null && self.AttachmentType.SizeLimit > 0;
		self.HasMinSize = self.AttachmentType && self.AttachmentType.LowerSizeLimit != null && self.AttachmentType.LowerSizeLimit > 0;
		let WxH = /^\d+x\d+$/;
		self.HasResolution = self.AttachmentType && self.AttachmentType.Resolution != null && WxH.test(self.AttachmentType.Resolution);
	}

	ngOnInit() {
		// Subscribe to screen changes
		this.screenSvc.ScreenSizeSub.pipe(takeUntil(this._subrelease))
			.subscribe(size => {
				this.SmallSize = size.IsSmallSize;
			});

		// Set initial size.
		this.SmallSize = this.screenSvc.ScreenSize.IsSmallSize;
	}

	DownloadFile() {
		const self = this;

		if (!self.NomineeType.FileStorageRoot || !self.AttachmentType.DownloadFile) { return; }

		let fileName = self.AttachmentType.DownloadFile;
		let fileStorageURL = self.NomineeType.FileStorageRoot;

		self.splashSvc.OpenSplash({
			TitleText: 'Please Wait',
			MessageText: `Downloading file...`
		}).pipe(takeUntil(self._subrelease)).subscribe(() => {
			self.downloadSvc.RetrieveDownload(HttpMethod.GET, 'Api/Services/App/DownloadFile', {
				AuthToken: self.User.AuthToken || '',
				FileName: fileName,
				FilePath: fileStorageURL
			}, DownloadFormat.PDF).pipe(takeUntil(self._subrelease)).subscribe(() => {
				self.splashSvc.CloseSplash();
			}, error => self.errSvc.HandleServiceError(error));
		});
	}

	HandleUpload (files: FileUploadInformation[]) {
		const self = this;
		if (!files || !files.length) { return; }
		const parsedFiles: FileUploadInformation[] = [];

		// Parse through and check to see if the file is over the size limit specified. If not, attach relevant parameters for the files
		for (let i = 0; i < files.length; i++) {
			this.validateUpload(files[i]).pipe(takeUntil(self._subrelease)).subscribe(fileInfo => {
				if (fileInfo.Valid === true) {
					const Params = {
						AuthToken: self.User.AuthToken,
						AwardID: self.AwardID,
						NominationFormUID: self.NomineeInfo.NominationFormUID,
						FileStorageURL: self.NomineeType.FileStorageRoot,
						OriginalFileName: fileInfo.File.name,
						TargetFileName: `${self.NomineeType.ShortName}-${self.SiteName}-` +
							`${self.NomineeInfo.FirstName} ${self.NomineeInfo.LastName}` +
							fileInfo.File.name.substring(fileInfo.File.name.lastIndexOf('.')),
						AttachmentTypeID: (self.AttachmentType && self.AttachmentType.AttachmentTypeID)
					};

					fileInfo.Parameters = Params;
					parsedFiles.push(fileInfo);
				}
				//if last iteration of loop
				if (i + 1 === files.length) {
					if (parsedFiles.length > 0) {
						this.packFiles(parsedFiles);
					}
				}
			});
		}
	}

	validateUpload(file: FileUploadInformation): Observable<FileUploadInformation> {
		let self = this;

		return new Observable((observer: Observer<FileUploadInformation>) => {
			if (this.HasMaxSize && file.File.size > self.AttachmentType.SizeLimit) {
				// Alert to the user that this file is over the limit.
				self.dlgSvc.OpenMessageBoxDialog({
					TitleText: 'File Over Size Limit',
					MessageText: `A file you are attempting to upload is over the designated file size limit ` +
						`for this type of file:<br /><br /><strong>File name: </strong>${file.File.name}` +
						`<br /><strong>File size: </strong>${self.fileSizePipe.transform(file.File.size)}` +
						`<br /><strong>Allowed maximum size: </strong>${self.fileSizePipe.transform(self.AttachmentType.SizeLimit)}` +
						`<br /><br />This file will not be uploaded to the system.`,
					OKText: 'OK'
				}).pipe(takeUntil(self._subrelease)).subscribe();

				file.Valid = false;
				observer.next(file);
				observer.complete();
			}

			if (this.HasMinSize && file.File.size < self.AttachmentType.LowerSizeLimit) {
				// Alert to the user that this file is under the limit.
				self.dlgSvc.OpenMessageBoxDialog({
					TitleText: 'File Under Size Limit',
					MessageText: `A file you are attempting to upload is under the designated file size limit ` +
						`for this type of file:<br /><br /><strong>File name: </strong>${file.File.name}` +
						`<br /><strong>File size: </strong>${self.fileSizePipe.transform(file.File.size)}` +
						`<br /><strong>Allowed minimum size: </strong>${self.fileSizePipe.transform(self.AttachmentType.LowerSizeLimit)}` +
						`<br /><br />This file will not be uploaded to the system.`,
					OKText: 'OK'
				}).pipe(takeUntil(self._subrelease)).subscribe();

				file.Valid = false;
				observer.next(file);
				observer.complete();
			}

			if (this.HasResolution) {
				let xIndex = this.AttachmentType.Resolution.indexOf('x');
				let photoW = +this.AttachmentType.Resolution.substring(0, xIndex);
				let photoH = +this.AttachmentType.Resolution.substring(xIndex + 1);
				let reader = new FileReader();
				reader.readAsDataURL(file.File);

				reader.onload = event => {
					let image = new Image();
					image.src = event.target.result.toString();

					image.onload = () => {
						if (image.width < photoW || image.height < photoH) {
							// Alert to the user that this file is under the limit.
							self.dlgSvc.OpenMessageBoxDialog({
								TitleText: 'Image Resolution Under Limit',
								MessageText: `A file you are attempting to upload is under the designated image resolution limit ` +
									`for this type of file:<br /><br /><strong>File name: </strong>${file.File.name}` +
									`<br /><strong>File resolution: </strong>${image.width + 'x' + image.height}` +
									`<br /><strong>Allowed minimum resolution: </strong>${self.AttachmentType.Resolution}` +
									`<br /><br />This file will not be uploaded to the system.`,
								OKText: 'OK'
							}).pipe(takeUntil(self._subrelease)).subscribe();

							file.Valid = false;
							observer.next(file);
							observer.complete();
						}
						else {
							file.Valid = true;
							observer.next(file);
							observer.complete();
						}
					};
				};
			}
			else {
				file.Valid = true;
				observer.next(file);
				observer.complete();
			}
		});
	}

	packFiles(parsedFiles: FileUploadInformation[]) {
		let self = this;

		// Initiate upload to get observables to monitor
		const uploads = self.uploadSvc.Upload(self.configSvc.hostAddress +
			`Api/Services/App/UploadFiles`, parsedFiles);

		// Subscribe to updates from the files being uploaded
		combineLatest(uploads).pipe(
			takeUntil(self._subrelease)
		).subscribe(status => {
			// Iterate through each of the returned update statuses, and either insert a new entry in the Videos
			// list (if this is the first upload), or update the progress/status of the entry if it's already there.
			if (status && !!status.length) {
				status.forEach(stat => {
					// Try to find a match to this entry's UploadID value.
					const entryIdx = (self.Files || []).findIndex(f => f.UploadID === stat.UploadID);

					if (entryIdx < 0 && stat.isInProgress) {
						// The entry doesn't exist. Add to the array holding all of the file info.
						const newFile = <FileAttachmentInfo>{
							FileName: null,
							FileSize: null,
							FileStorageURL: null,
							FileStatus: 'Uploading',
							FileAttachmentID: null,
							IsInProgress: stat.isInProgress,
							OriginalFileName: stat.FileName,
							Uploader: null,
							UploaderName: null,
							DateUploaded: null,
							UploadID: stat.UploadID,
							UploadPercentComplete: stat.PercentComplete
						};

						self.Files.push(newFile);
						self.NoData = false; // Can't be no data when we're uploading a file right now.
					} else if (entryIdx >= 0) {
						// Got the matching entry. Update the necessary fields.
						const entryval = self.Files[entryIdx];

						if (entryval) {
							entryval.UploadPercentComplete = stat.PercentComplete;
							entryval.IsInProgress = stat.isInProgress;

							if (stat.isComplete) {
								entryval.FileStatus = 'Uploaded';

								if (stat.isError) {
									// Splash error.
									self.splashSvc.SnackbarError(stat.Error, 10000);
									self.Files.pop();
									if (self.Files.length == 0) {
										self.NoData = true;
									}
								}

								if (stat.FinalFileInfo) {
									// Update this photo entry's information for display.
									entryval.AttachmentTypeID = stat.FinalFileInfo.AttachmentTypeID;
									entryval.DateUploaded = stat.FinalFileInfo.DateUploaded;
									entryval.FileAttachmentID = stat.FinalFileInfo.FileAttachmentID;
									entryval.FileName = stat.FinalFileInfo.FileName;
									entryval.FileSize = stat.FinalFileInfo.FileSize;
									entryval.FileStorageURL = stat.FinalFileInfo.FileStorageURL;
									entryval.FileTypeCode = stat.FinalFileInfo.FileTypeCode;
									entryval.FileTypeName = stat.FinalFileInfo.FileTypeName;
									entryval.NominationFormUID = stat.FinalFileInfo.NominationFormUID;
									entryval.OriginalFileName = stat.FinalFileInfo.OriginalFileName;
									entryval.Uploader = stat.FinalFileInfo.Uploader;
									entryval.UploaderName = stat.FinalFileInfo.UploaderName;
									entryval.UploadID = null;

									// Update the attached FilesInfo object with the updated files listing
									if (self.FilesInfo) {
										const _files = self.FilesInfo.find(fi => fi.AttachmentTypeID === stat.FinalFileInfo.AttachmentTypeID);

										if (_files) {
											_files.Files = self.Files;
										} else {
											// It didn't exist. Add it now.
											self.FilesInfo.push(<NomineeFileInfo>{
												AttachmentTypeID: stat.FinalFileInfo.AttachmentTypeID,
												Code: stat.FinalFileInfo.FileTypeCode,
												NominationFormUID: stat.FinalFileInfo.NominationFormUID,
												Files: self.Files
											});
										}
									}

									// Emit the event for Angular to update the two-way binding on the file info
									self.FilesInfoChange.emit(self.FilesInfo);

									// Emit an upload complete event if any parent component is interested.
									self.uploaded.emit(entryval);

									// Splash success.
									self.splashSvc.SnackbarSuccess('File uploaded successfully', 5000);
								}
							}
						}
					}
				});
			}
		}, error => {
			self.dlgSvc.OpenMessageBoxDialog({
				TitleText: 'File Upload Error',
				MessageText: `There was an error during the file upload request. The error information is ` +
					`shown below:<br /><br />${error}`,
				OKText: 'OK'
			}).pipe(takeUntil(self._subrelease)).subscribe();
		}, () => { });
	}

	RemoveFile(file: FileAttachmentInfo) {
		const self = this;

		if (!file) { return; }

		// Get a confirmation before proceeding with the removal.
		self.dlgSvc.OpenConfirmationDialog({
			TitleText: 'File Removal Confirmation Requested',
			MessageText: `YOu are about to remove the following file from the system:<br /><br />` +
				`<strong>File name: </strong>${file.FileName}<br /><strong>File size: </strong>` +
				`${self.fileSizePipe.transform(file.FileSize, 2)}<br /><strong>File type: </strong>` +
				`${file.FileTypeName}<br /><strong>Upload date: </strong>` +
				`${self.dateFnsPipe.transform(file.DateUploaded, 'M/d/yy h:mm a')}<br /><br />Once removed, this file will ` +
				`have to be uploaded again in the future in order to be part of this nomination form. ` +
				`Are you sure you wish to continue?`,
			OKText: 'Yes - Remove File',
			CancelText: 'No - Cancel'
		}).pipe(takeUntil(self._subrelease)).subscribe(ok => {
			if (ok === true) {
				// User opted to remove the file. Process the request.
				self.appSvc.DeleteFile(self.User.AuthToken, file.FileAttachmentID, file.FileStorageURL,
					file.AttachmentTypeID).pipe(takeUntil(self._subrelease))
					.subscribe(result => {
						if (result) {
							// Successful delete. Remove the entry from the list of files for this nomination and attachment type.
							if (self.FilesInfo) {
								const _files = self.FilesInfo.find(fi => fi.AttachmentTypeID === file.AttachmentTypeID);

								if (_files && _files.Files && !!_files.Files.length) {
									_files.Files = _files.Files.filter(f => f.FileAttachmentID !== file.FileAttachmentID);
									self.Files = _files.Files;
								}
							}

							// Push out the bound change.
							self.NoData = (!self.Files || !self.Files.length);
							self.FilesInfoChange.emit(self.FilesInfo);

							// Notify any subscribed parent components that we've removed this file.
							self.removed.emit(file);

							// Toast success.
							self.splashSvc.SnackbarSuccess('File removed successfully.');
						}
					}, error => self.errSvc.HandleServiceError(error)
					);
			}
		});
	}


}



export class RemoveFileEvent {
	Nominee?: NomineeInfo;
	File?: FileAttachmentInfo;
}

